import {
  EnrichedRiskArea,
  RiskLevel,
  RiskStatus,
  FactorSeverity,
} from '../types/prevention'
import { PROJECT_REGION_MAP } from './regionData'

// Simple seeded random number generator (Mulberry32)
function createSeededRandom(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const generateHistory = (
  baseScore: number,
  trend: 'up' | 'down' | 'flat',
  random: () => number,
  days: number = 14,
) => {
  const history = []
  let currentScore =
    trend === 'up'
      ? baseScore - 20
      : trend === 'down'
        ? baseScore + 20
        : baseScore

  for (let i = days; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)

    if (trend === 'up') {
      currentScore += random() * 4 - 1 // mostly up
    } else if (trend === 'down') {
      currentScore -= random() * 4 - 1 // mostly down
    } else {
      currentScore += random() * 4 - 2 // flat
    }

    // Cap between 10 and 99
    currentScore = Math.max(10, Math.min(99, currentScore))

    history.push({
      date,
      score: Math.round(currentScore),
    })
  }

  // Ensure the last score matches the baseScore exactly
  history[history.length - 1].score = baseScore

  return history
}

export const ACTIVITIES_BY_TRADE = [
  {
    trade: 'Structural Steel',
    activities: [
      'Set Base Plates, Shim Packs, and Column Splices',
      'Fly Structural Steel Beams at Podium and Typical Floors',
      'Bolt-Up and Torque Moment Connections',
      'Set Open-Web Joists and Install Bridging',
      'Install Composite Metal Deck and Weld Shear Studs',
    ],
  },
  {
    trade: 'Concrete/Formwork',
    activities: [
      'Fly Gang Forms at Core Walls',
      'Install Rebar Mats, Couplers, and Embed Plates',
      'Set Slab Edge Forms and Blockouts',
      'Pump, Place, and Finish Elevated Slab Concrete',
      'Stress Post-Tension Tendons and Cut Tails',
    ],
  },
  {
    trade: 'Excavation/Earthwork',
    activities: [
      'Bulk Excavation for Below-Grade Parking',
      'Install Soldier Piles and Lagging at Cut Face',
      'Trim Subgrade and Proof-Roll Building Pad',
      'Excavate Utility Trench and Set Trench Box',
      'Backfill and Compact in Controlled Lifts',
    ],
  },
  {
    trade: 'Mechanical/HVAC',
    activities: [
      'Set Rooftop Air Handling Unit by Crane',
      'Hang Spiral Duct Mains Above Corridor Ceilings',
      'Install Chilled Water and Heating Hot Water Mains',
      'Set VAV Boxes and Flex Duct Branches',
      'Pressure Test Hydronic Piping and Clean Strainers',
    ],
  },
  {
    trade: 'Electrical',
    activities: [
      'Install Temporary Power Spider Boxes and Lighting Strings',
      'Rough-In Conduit in Deck, Walls, and Slab',
      'Pull Feeders Through Riser and Bus Duct',
      'Set Switchgear, Panels, and MCC Lineups',
      'Terminate Devices, Fire Alarm Loops, and Low-Voltage Cabling',
    ],
  },
  {
    trade: 'Plumbing/Fire Protection',
    activities: [
      'Hang Cast Iron Sanitary Stack in Shaft',
      'Install Domestic Water Mains and Booster Pump Tie-Ins',
      'Groove and Hang Fire Sprinkler Branch Lines',
      'Set Carrier Assemblies and In-Wall Plumbing',
      'Hydro Test Sanitary, Water, and Sprinkler Systems',
    ],
  },
  {
    trade: 'Roofing/Waterproofing',
    activities: [
      'Install Air/Vapor Barrier and Tapered Insulation',
      'Set Roof Drains, Overflows, and Sumps',
      'Fully Adhere TPO Membrane at Low Roof',
      'Apply Hot Fluid Waterproofing at Plaza Deck',
      'Flash Curbs, Penetrations, and Parapet Conditions',
    ],
  },
  {
    trade: 'Curtain Wall/Glazing',
    activities: [
      'Offload and Rack Unitized Curtain Wall Panels',
      'Set Embed Receivers and Shim Slab-Edge Anchors',
      'Install Unitized Panels from Monorail or Mini-Crane',
      'Glaze Podium Storefront and Entrance Assemblies',
      'Seal Perimeter Joints and Perform Water Testing',
    ],
  },
  {
    trade: 'Interior Finishes',
    activities: [
      'Frame Shaft Wall, Corridor Soffits, and Bulkheads',
      'Hang, Tape, and Finish Level 4 Drywall',
      'Set ACT Grid, Clouds, and Specialty Ceilings',
      'Install Hollow Metal Frames, Doors, and Hardware',
      'Install LVT, Tile, Base, and Resilient Finish Flooring',
    ],
  },
  {
    trade: 'Demolition',
    activities: [
      'Selective Interior Demo to Deck and Structure',
      'Sawcut and Remove Slab Opening for New Penetrations',
      'Remove Existing MEP Overhead in Active Renovation Zone',
      'Demo CMU Walls and Segregate Debris Streams',
      'Torch Cut Miscellaneous Structural Steel and Embedments',
    ],
  },
  {
    trade: 'Crane/Rigging',
    activities: [
      'Assemble, Jump, and Climb Tower Crane',
      'Rig and Fly Rebar Cages to Core',
      'Dual-Crane Lift of Chiller or Generator Skid',
      'Set Prefabricated MEP Racks Through Floor Openings',
      'Pick and Set Transformer, Switchgear, and Pad Equipment',
    ],
  },
  {
    trade: 'Site Work/Utilities',
    activities: [
      'Install Storm Drain Manholes and RCP',
      'Place Ductbank Conduits and Concrete Encasement',
      'Set Underground Detention Chambers and Stone Backfill',
      'Tie-In Domestic Water and Fire Service to Street Main',
      'Fine Grade, Form, and Pour Sidewalk and Curb',
    ],
  },
]

const ALL_ACTIVITIES = ACTIVITIES_BY_TRADE.flatMap((t) => t.activities)

const CONTACT_NAMES = [
  'Mike Reilly',
  'Carlos Mendez',
  'Dave Kowalski',
  'James Thornton',
  'Tony Russo',
  "Brian O'Connell",
  'Luis Ramirez',
  'Steve Novak',
  'Kevin Patel',
  'Chris Brennan',
  'Maria Santos',
  'Angela Torres',
  'Rob Fitzgerald',
  'Dan Morales',
  'Pat Sullivan',
]

const CONTACT_ROLES = [
  'Superintendent',
  'General Foreman',
  'Safety Manager',
  'Project Engineer',
  'Field Engineer',
  'Trade Foreman',
  'Site Safety Officer',
  'Assistant Superintendent',
]

const CONTRACTORS = [
  'Northeast Steel Erectors',
  'Empire City Concrete',
  'Frontier Earthworks',
  'Capital Air Balance & Sheet Metal',
  'Blue Ridge Electric Systems',
  'Carolina Utility Services',
  'Gulf Coast Roofing',
  'Pacific Rim Glazing',
  'Monument Interiors',
  'Great Lakes Demo',
  'Sunbelt Crane Services',
  'Delta Utility Installers',
  'Keystone Ironworks',
  'Metro Waterproofing Group',
  'Tri-State Masonry Restoration',
  'Capital Mechanical Works',
  'Highline Electrical Constructors',
  'Harbor Trucking & Disposal',
  'Allegheny Decking Systems',
  'Summit Fireproofing',
  'Heritage Abatement Group',
  'Precision Mechanical Assemblies',
  'Mid-Atlantic Site Logistics',
  'Sunbelt Concrete Placers',
  'Great Lakes Rigging',
  'Liberty Concrete Rehab',
  'ModCore Interiors',
  'Beacon Interiors',
  'Mesa Trench Support',
  'Urban Crane & Hoist',
]

const LOCATIONS = [
  'North Elevation',
  'South Elevation',
  'East Wing',
  'West Wing',
  'Core Area',
  'Podium Level',
  'Mechanical Room',
  'Loading Dock',
  'Stair Tower B',
  'Elevator Shaft C',
  'Roof Level',
  'Plaza Deck',
  'Parking Garage',
  'Amenity Floor',
  'Lobby',
]

const RISK_FACTORS = [
  'High winds forecasted (35+ mph) — crane and scaffold operations at risk',
  'New workers (< 30 days) make up 29% of current workforce on affected floors',
  'Only 4 of 12 PTPs included hazard-specific controls this week',
  'Nine ladder-use observations logged in 14 days in stair towers and above-ceiling zones',
  'Three required scaffold inspections missed this week on south elevation',
  'Seven struck-by observations recorded below curtain wall loading bays in 10 days',
  'Twelve pedestrian-equipment interaction observations near loading docks',
  'Two near misses involving unstable loads during prefab rack picks',
  'Eleven damaged-cord or spider-box conditions found across three active levels',
  'Competent-person documentation inconsistent on 5 of last 9 trench entries',
  'Dust-related observations doubled since dry core drilling began on amenity floors',
  'Back-to-back fatigue and dizziness first-aid cases during afternoon deck pours',
  'Four recordable incidents remain open past investigation due date',
  'Three hand-injury events with similar root causes in six weeks across two trades',
  'Reinspection completion fell below trend for two consecutive weeks',
]

const ACTIONS = [
  {
    action:
      'Require superintendent sign-off on every high-risk PTP through Friday',
    rationale:
      'Ensures task-specific controls are reviewed before work begins on elevated, lifting, or energized scopes.',
    expectedImpactPercent: 20,
  },
  {
    action:
      'Stop work in any high-risk area without current required inspection on file',
    rationale:
      'Restores basic assurance checks and prevents high-risk areas from operating without current review.',
    expectedImpactPercent: 35,
  },
  {
    action:
      'Assign senior field leaders to the three highest-risk hotspots for the next five workdays',
    rationale:
      'Puts leadership attention on the conditions most likely to cause severe harm.',
    expectedImpactPercent: 25,
  },
  {
    action:
      'Pull unverified workers from high-risk tasks until competency records are confirmed',
    rationale:
      'Reduces execution errors on tasks where unfamiliarity with site rules creates immediate exposure.',
    expectedImpactPercent: 30,
  },
  {
    action:
      'Create a daily burn-down list for open observations older than seven days',
    rationale:
      'Reduces repeated exposure and keeps the observation process functioning as a control tool.',
    expectedImpactPercent: 15,
  },
  {
    action:
      'Cap high-risk work after 10 hours and rotate physically demanding tasks',
    rationale:
      'Improves decision quality late in the day and reduces error-prone fatigue conditions.',
    expectedImpactPercent: 20,
  },
  {
    action:
      'Limit concurrent equipment in tightest zones and separate pedestrian routes with hard barricades',
    rationale:
      'Reduces struck-by exposure and improves predictability of site movements during peak logistics.',
    expectedImpactPercent: 30,
  },
  {
    action:
      'Run a 30-minute PTP writing refresher for all foremen on task-step hazards and controls',
    rationale:
      'Addresses the root cause of generic PTPs by improving foreman capability at the source.',
    expectedImpactPercent: 18,
  },
]

// Region-based risk bias: controls the probability thresholds for risk levels
// Higher thresholds for critical/high = more risky region
const REGION_RISK_BIAS: Record<
  string,
  { critical: number; high: number; medium: number }
> = {
  northeast: { critical: 0.04, high: 0.25, medium: 0.65 }, // High risk region
  mid_atlantic: { critical: 0.0, high: 0.08, medium: 0.45 }, // Low risk region
  southeast: { critical: 0.02, high: 0.15, medium: 0.55 }, // Mixed
  midwest: { critical: 0.0, high: 0.03, medium: 0.35 }, // Very safe region
  southwest: { critical: 0.05, high: 0.28, medium: 0.7 }, // Higher risk region
  west_coast: { critical: 0.0, high: 0.06, medium: 0.4 }, // Mostly safe
}

function generateMockAreas(): EnrichedRiskArea[] {
  const areas: EnrichedRiskArea[] = []
  let globalId = 1

  PROJECT_REGION_MAP.forEach((projectMap, projectIndex) => {
    // Seed based on project index so it's deterministic
    const random = createSeededRandom(projectIndex * 1337)

    // 4 to 30 activities per project (varied distribution)
    const numActivities = Math.floor(random() * 27) + 4

    const bias = REGION_RISK_BIAS[projectMap.regionId] || {
      critical: 0.15,
      high: 0.4,
      medium: 0.75,
    }

    for (let i = 0; i < numActivities; i++) {
      const randVal = random()
      let riskLevel: RiskLevel
      let riskScore: number
      let sifPotential = false

      if (randVal < bias.critical) {
        riskLevel = 'critical'
        riskScore = Math.floor(random() * 15) + 85 // 85-99
        sifPotential = random() < 0.08
      } else if (randVal < bias.high) {
        riskLevel = 'high'
        riskScore = Math.floor(random() * 15) + 70 // 70-84
        sifPotential = random() < 0.03
      } else if (randVal < bias.medium) {
        riskLevel = 'medium'
        riskScore = Math.floor(random() * 20) + 50 // 50-69
      } else {
        riskLevel = 'low'
        riskScore = Math.floor(random() * 40) + 10 // 10-49
      }

      const severities: FactorSeverity[] = ['none', 'low', 'medium', 'high']
      const getSeverity = () =>
        severities[Math.floor(random() * severities.length)]

      const statusOptions: RiskStatus[] = [
        'open',
        'measures_taken',
        'closed',
        'monitoring',
      ]
      const status = statusOptions[Math.floor(random() * statusOptions.length)]

      const contractor = CONTRACTORS[Math.floor(random() * CONTRACTORS.length)]
      const location = LOCATIONS[Math.floor(random() * LOCATIONS.length)]
      // Pick a trade-specific activity as the "floor" label (used as activity name in the matrix)
      const tradeGroup =
        ACTIVITIES_BY_TRADE[Math.floor(random() * ACTIVITIES_BY_TRADE.length)]
      const activityName =
        tradeGroup.activities[
          Math.floor(random() * tradeGroup.activities.length)
        ]
      const floor = activityName

      const numFactors = Math.floor(random() * 3) + 1
      const factors = []
      for (let j = 0; j < numFactors; j++) {
        factors.push(RISK_FACTORS[Math.floor(random() * RISK_FACTORS.length)])
      }

      const numActions = Math.floor(random() * 2) + 1
      const correctiveActions = []
      for (let j = 0; j < numActions; j++) {
        const actionTpl = ACTIONS[Math.floor(random() * ACTIONS.length)]
        correctiveActions.push({
          id: `a${globalId}_${j}`,
          action: actionTpl.action,
          priority:
            riskLevel === 'critical' || riskLevel === 'high'
              ? 'immediate'
              : 'high',
          expectedImpactPercent: actionTpl.expectedImpactPercent,
          rationale: actionTpl.rationale,
        })
      }

      const trendOptions: ('up' | 'down' | 'flat')[] = ['up', 'down', 'flat']
      const trend = trendOptions[Math.floor(random() * trendOptions.length)]

      areas.push({
        id: `area_${globalId++}`,
        project: projectMap.projectName,
        floor,
        location,
        riskLevel,
        riskScore,
        riskFactorScores: {
          weather: getSeverity(),
          inspections: getSeverity(),
          workers: getSeverity(),
          incidents: getSeverity(),
          documents: getSeverity(),
          ptpQuality: getSeverity(),
          ptpEngagement: getSeverity(),
        },
        status,
        contractors: [contractor],
        riskFactors: factors,
        dataSources: [
          {
            id: `ds_${globalId}`,
            type: 'weather',
            name: 'System Alert',
            description: 'Automated risk detection',
          },
        ],
        correctiveActions,
        contacts: [
          {
            name: CONTACT_NAMES[Math.floor(random() * CONTACT_NAMES.length)],
            role: CONTACT_ROLES[Math.floor(random() * CONTACT_ROLES.length)],
            company: contractor,
            phone: `(${Math.floor(random() * 900) + 100}) ${Math.floor(random() * 900) + 100}-${Math.floor(random() * 9000) + 1000}`,
          },
        ],
        lastUpdated: new Date(Date.now() - random() * 1000 * 60 * 60 * 24),
        scheduledDate: new Date(),
        scoreHistory: generateHistory(riskScore, trend, random),
        events: [
          {
            id: `evt_${globalId}`,
            date: new Date(Date.now() - random() * 1000 * 60 * 60 * 12),
            type: 'system_alert',
            description: 'Risk level updated based on new signals',
            scoreImpact: Math.floor(random() * 10) + 1,
          },
        ],
        sifPotential,
        dataConfidence: random() > 0.2 ? 'high' : 'medium',
        auditTrail: [],
        relatedIncidents: [],
      })
    }
  })

  return areas
}

export const ENRICHED_MOCK_RISK_AREAS: EnrichedRiskArea[] = generateMockAreas()
